/**
 * Support Ticket Type Definitions
 *
 * TypeScript types for support ticket entities and operations.
 * Mirrors backend models from tenant-api.
 */

// ============================================================================
// ENUMS
// ============================================================================

export const SupportTicketStatus = {
  OPEN: 'open',
  IN_PROGRESS: 'in_progress',
  AWAITING_CUSTOMER: 'awaiting_customer',
  RESOLVED: 'resolved',
  CLOSED: 'closed',
} as const;

export type SupportTicketStatus = typeof SupportTicketStatus[keyof typeof SupportTicketStatus];

export const SupportTicketPriority = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  CRITICAL: 'critical',
} as const;

export type SupportTicketPriority = typeof SupportTicketPriority[keyof typeof SupportTicketPriority];

export const SupportTicketCategory = {
  BUG: 'bug',
  FEATURE_REQUEST: 'feature_request',
  BILLING: 'billing',
  TECHNICAL_SUPPORT: 'technical_support',
  DATA_ISSUE: 'data_issue',
  OTHER: 'other',
} as const;

export type SupportTicketCategory = typeof SupportTicketCategory[keyof typeof SupportTicketCategory];

// ============================================================================
// ATTACHMENT TYPES
// ============================================================================

export interface TicketAttachment {
  attachmentId: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  s3Key: string;
  uploadedBy: string;
  uploadedByType: 'TENANT_ADMIN' | 'PLATFORM_ADMIN';
  createdAt: string;
}

// ============================================================================
// SUPPORT TICKET ENTITY
// ============================================================================

export interface SupportTicket {
  tenantId: string;
  ticketId: string;
  referenceNumber: string;

  // Content
  subject: string;
  category: SupportTicketCategory;
  priority: SupportTicketPriority;
  status: SupportTicketStatus;

  // Related application (optional)
  applicationId?: string;
  applicationName?: string;

  // Creator info
  createdBy: string;
  createdByEmail: string;
  createdByName: string;

  // Assignment
  assignedTo?: string;
  assignedToName?: string;
  assignedAt?: string;

  // SLA tracking
  slaDeadline: string;
  slaBreach: boolean;
  firstResponseAt?: string;

  // Resolution
  resolvedAt?: string;
  closedAt?: string;

  // Message tracking
  messageCount: number;
  lastMessageAt?: string;
  lastMessageBy?: string;
  lastMessageByType?: 'TENANT_ADMIN' | 'PLATFORM_ADMIN';

  // Timestamps
  createdAt: string;
  updatedAt: string;
}

export type SupportTicketSanitized = SupportTicket;

// ============================================================================
// TICKET MESSAGE ENTITY
// ============================================================================

export interface TicketMessage {
  tenantId: string;
  ticketId: string;
  messageId: string;

  // Author
  authorId: string;
  authorType: 'TENANT_ADMIN' | 'PLATFORM_ADMIN';
  authorName: string;
  authorEmail: string;

  // Content
  content: string;
  isInternal: boolean;

  // Attachments
  attachments: TicketAttachment[];

  // Timestamps
  createdAt: string;
  updatedAt: string;
}

export type TicketMessageSanitized = TicketMessage;

// ============================================================================
// DTOs
// ============================================================================

export interface SupportTicketCreateDTO {
  subject: string;
  category: SupportTicketCategory;
  priority: SupportTicketPriority;
  description: string;
  applicationId?: string;
}

export interface TicketMessageCreateDTO {
  content: string;
  attachmentIds?: string[];
}

// ============================================================================
// QUERY TYPES
// ============================================================================

export interface SupportTicketListQuery {
  status?: SupportTicketStatus;
  priority?: SupportTicketPriority;
  category?: SupportTicketCategory;
  limit?: number;
  cursor?: string;
}

// ============================================================================
// RESPONSE TYPES
// ============================================================================

export interface SupportTicketWithMessage extends SupportTicketSanitized {
  initialMessage?: TicketMessageSanitized;
}

export interface TicketMessagesResponse {
  items: TicketMessageSanitized[];
  count: number;
}

export interface UploadUrlResponse {
  uploadUrl: string;
  attachmentId: string;
  expiresIn: number;
}

export interface DownloadUrlResponse {
  downloadUrl: string;
  fileName: string;
  expiresIn: number;
}

// ============================================================================
// DISPLAY HELPERS
// ============================================================================

export const statusLabels: Record<SupportTicketStatus, string> = {
  open: 'Open',
  in_progress: 'In Progress',
  awaiting_customer: 'Awaiting Response',
  resolved: 'Resolved',
  closed: 'Closed',
};

export const statusColors: Record<SupportTicketStatus, string> = {
  open: 'bg-blue-100 text-blue-800',
  in_progress: 'bg-yellow-100 text-yellow-800',
  awaiting_customer: 'bg-purple-100 text-purple-800',
  resolved: 'bg-green-100 text-green-800',
  closed: 'bg-gray-100 text-gray-800',
};

export const priorityLabels: Record<SupportTicketPriority, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  critical: 'Critical',
};

export const priorityColors: Record<SupportTicketPriority, string> = {
  low: 'bg-gray-100 text-gray-800',
  medium: 'bg-blue-100 text-blue-800',
  high: 'bg-orange-100 text-orange-800',
  critical: 'bg-red-100 text-red-800',
};

export const categoryLabels: Record<SupportTicketCategory, string> = {
  bug: 'Bug Report',
  feature_request: 'Feature Request',
  billing: 'Billing',
  technical_support: 'Technical Support',
  data_issue: 'Data Issue',
  other: 'Other',
};
